import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import usePlayfulNav from '@/hooks/usePlayfulNav';
import toast from 'react-hot-toast';
import { useSession } from '@/hooks/useSession';
import { FiCamera, FiLogOut, FiShield } from 'react-icons/fi';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import promiseRequest from '@/utils/promiseToast';
import Onboarding from '@/components/custom/onboarding';
import { Loader } from '@/components/custom/loader';
import { validateCPF } from '@/utils/validateCPF';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardContent } from '@/layouts/dashboard';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { TypographyDisclaimer } from '@/components/custom/typography';
import { AvatarWrapper } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Input, InputMask, InputPassword } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ProfileForm, ProfileInfo, ProfileSidebar } from '@/components/custom/profile';
import { FormCheck, FormField, FormSwitch } from '@/components/custom/form';
import { Textarea } from '@/components/ui/textarea';

export function Profile(props) {
  const { t } = useTranslation();
  const { promiseToast, isSubmitting } = promiseRequest();
  const { session, sanitizeResponse, setSession, signOut } = useSession();
  const client = useClient();
  const playfulStudent = usePlayfulStudent();
  // Quem navega sem sidebar (aluno E professor) não tem outro logout na tela.
  const playfulNav = usePlayfulNav();
  const [values, setValues] = useState({
    name: session.user?.name,
    email: session.user?.email,
    bio: session.user?.bio,
    phone: session.user?.userDetail?.phone,
    cpf_cnpj: session?.user?.cpfCnpj
  });
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [userDetail, setUserDetail] = useState();
  const userId = session.user.id;
  const { path } = props;
  const profiles = session?.user?.profileNames || (session?.user && session?.user['profile-names']) || [];

  const formik = useFormik({
    initialValues: {
      password: '',
      newPassword: '',
      confirmPassword: ''
    },
    onSubmit: async values => {
      const payloadParams = {
        password: values.newPassword,
        password_confirmation: values.confirmPassword,
        old_password: values.password
      };
      const { error } = await client.mutate(`/users/${userId}`, payloadParams);
      if (error) {
        toast.error(t('toast.errorUpdatePass'));
      } else {
        toast.success(t('toast.successUpdatePass'));
      }
    },
    validationSchema: yup.object({
      password: yup.string().required(t('profile.typeYourPass')),
      newPassword: yup.string().required(t('profile.typeYourNewPass')),
      confirmPassword: yup
        .string()
        .required(t('profile.confirmNewPass'))
        .oneOf([yup.ref('newPassword')], t('profile.passMustMatch'))
    })
  });

  const updateUser = e => {
    e.preventDefault();
    const payloadParams = {
      ...values,
      userDetailAttributes: {
        id: session.user?.userDetail?.id,
        phone: values.phone,
        hoursByWeek: 40
      }
    };
    delete payloadParams.phone;

    promiseToast({
      url: ['users', session.user.id],
      request: payloadParams,
      successText: t('toast.successUpdateProfile'),
      errorText: t('toast.error')
    })
      .then(data => {
        const newObject = sanitizeResponse(data);
        setSession({ user: newObject });
      })
      .catch(error => {
        toast.error(`Ops, ${error.id} ${error.title}`);
      });
  };

  const handleChange = e => {
    let auxValues = values;
    auxValues[e.target.name] = e.target.value;
    setValues({ ...auxValues });
  };

  const handleChangeImage = e => {
    let file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = _handleReaderLoaded.bind(this);
      reader.readAsBinaryString(file);
    }
  };

  const _handleReaderLoaded = readerEvt => {
    let bynaryString = readerEvt.target.result;
    let auxValues = values;
    auxValues['image'] = `data:image/jpeg;base64,${btoa(bynaryString)}`;
    setValues({ ...auxValues });
    // document.getElementById('profileForm')
    // .dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const getNotificationSettings = async () => {
    const { data, error } = await client.fetch('notification_settings');
    if (error || data == null) {
      const { data: dataCreation, error: errorCreation } = await client.mutate('notification_settings');
      if (errorCreation || dataCreation == null) {
        toast.error(t('toast.errorGetNotificationSettings'));
      } else {
        notificationsFormik.setValues(dataCreation);
      }
    } else {
      notificationsFormik.setValues(data);
    }
  };

  const updateNotificationSettings = async fields => {
    const requestParams = {
      attributes: {
        ...fields
      }
    };
    const { data, error } = await client.mutate(['notification_settings', notificationsFormik?.values?.id], requestParams);
    if (error || data == null) {
      toast.error(t('toast.errorUpdateNotificationSettings'));
    } else {
      toast.success(t('toast.successUpdateNotifications'));
      notificationsFormik.setValues(data);
    }
  };

  const handleNotificationsChange = fieldName => async newCheckedState => {
    // 1. Atualiza o Formik para o campo específico com o novo estado booleano
    notificationsFormik.setFieldValue(fieldName, newCheckedState);

    // 2. Chame sua função de atualização de settings com o campo e o novo valor
    //    Use 'await' para garantir que a atualização seja enviada após o Formik ser atualizado
    await updateNotificationSettings({
      [fieldName]: newCheckedState // Cria um objeto dinamicamente { 'campo-x': true/false }
    });
  };

  const notificationsFormik = useFormik({
    initialValues: {
      'post-create': true,
      'event-create': true,
      'slot-create': true,
      'activity-create': true,
      'project-create': true,
      'content-create': true
    },
    onSubmit: () => {}
  });

  const updateUserDetail = async body => {
    const payloadParams = {
      hoursByWeek: 40,
      ...body
    };

    const { data, error } = await client.mutate(['user_details', session.user.userDetail.id], payloadParams);

    if (error) {
      toast.error(t('toast.errorUpdateNotificationSettings'));
    } else {
      toast.success(t('toast.successUpdateNotifications'));
      setUserDetail(data);
    }
  };

  const getUserDetail = async () => {
    setLoading(true);
    const { data, error } = await client.fetch('user_details');
    if (error || data == null) {
      toast.error(t('toast.errorGetNotificationSettings'));
    } else {
      setUserDetail(data);
    }
    setLoading(false);
  };

  const updatePassword = async event => {
    event.preventDefault();

    if (newPassword !== confirmPassword) {
    }
    const payloadParams = {
      password: newPassword,
      password_confirmation: confirmPassword,
      old_password: password
    };

    const { error } = await client.mutate(`/users/${userId}`, payloadParams);
    if (error) {
      toast.error(t('toast.errorUpdatePass'));
    } else {
      toast.success(t('toast.successUpdatePass'));
    }
  };

  const [cpfError, setCpfError] = useState(false);
  const handleBlur = () => {
    if (values?.cpf_cnpj?.replaceAll('_', '').length === 14 && !validateCPF(values.cpf_cnpj)) {
      setCpfError(true);
    } else {
      setCpfError(false);
    }
  };

  useEffect(() => {
    getUserDetail();
    getNotificationSettings();
  }, []);

  const breadCrumbs = {
    title: 'Perfil',
    nav: [
      {
        route: '/',
        name: values?.name,
        isActive: true
      }
    ]
  };

  if (loading)
    return (
      <DashboardContent>
        <Loader />
      </DashboardContent>
    );

  // Aba ativa em laranja sólido + texto branco no tema infantil (corporate mantém o pill claro).
  // Aba ativa chanfrada (relevo 3D igual aos botões): laranja + texto branco + lip escuro embaixo.
  const playfulTab = playfulStudent
    ? 'data-[state=active]:bg-[var(--color-mission)] data-[state=active]:text-white data-[state=active]:font-extrabold data-[state=active]:shadow-[0_3px_0_var(--color-mission-dark)] data-[state=active]:-translate-y-px'
    : undefined;

  const lgpdContent = (
    <>
      {t('profile.disclaimerLGPD')}{' '}
      <a
        className="hover:underline"
        href="/termos-de-uso"
        target="_blank"
        rel="noopener noreferrer"
      >
        termos de uso
      </a>{' '}
      e{' '}
      <a
        className="hover:underline"
        href="/politica-de-privacidade"
        target="_blank"
        rel="noopener noreferrer"
      >
        política de privacidade
      </a>
      .
    </>
  );

  return (
    <DashboardContent breadcrumbs={breadCrumbs}>
      <Tabs
        defaultValue="profileInformations"
        className="w-full"
      >
        <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
          <TabsList className={playfulStudent ? 'border border-neutral-lighter bg-white' : undefined}>
            <TabsTrigger
              value="profileInformations"
              className={playfulTab}
            >
              Informações de perfil
            </TabsTrigger>
            <TabsTrigger
              value="notifications"
              className={playfulTab}
            >
              Notificações
            </TabsTrigger>
            <TabsTrigger
              value="security"
              className={playfulTab}
            >
              Segurança
            </TabsTrigger>
          </TabsList>
          {/* Fora da sidebar este é o ÚNICO logout da tela — por isso o gate é o
              usePlayfulNav (aluno + professor) e não o usePlayfulStudent. A
              pílula 3D, essa sim, continua exclusiva do aluno. */}
          {playfulNav && (
            <button
              type="button"
              onClick={() => signOut()}
              className={
                playfulStudent
                  ? 'group/btn relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full px-6 py-2.5 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]'
                  : 'inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-neutral-lighter px-5 py-2.5 text-sm font-semibold text-neutral-dark transition-colors hover:bg-neutral-lighter'
              }
              style={
                playfulStudent
                  ? {
                      background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-primary) 88%, #fff) 0%, var(--color-primary) 100%)',
                      boxShadow: '0 4px 0 color-mix(in srgb, var(--color-primary) 62%, #000), 0 6px 14px rgba(0,0,0,0.16)'
                    }
                  : undefined
              }
            >
              {playfulStudent ? (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                  style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
                />
              ) : (
                <FiLogOut
                  aria-hidden="true"
                  className="h-4 w-4"
                />
              )}
              <span className="relative">Sair</span>
            </button>
          )}
        </div>

        <Page className="[&_[role=tabpanel]]:mt-0 [&_[role=tabpanel]]:w-full">
          <PageContent bg>
            <TabsContent value="profileInformations">
              <ProfileInfo>
                <ProfileSidebar>
                  <Label
                    customFileInput
                    className="relative w-fit"
                  >
                    <Input
                      type="file"
                      name="image"
                      id="image"
                      accept=".jpeg, .png, .jpg"
                      onChange={handleChangeImage}
                    />
                    <AvatarWrapper
                      size="lg"
                      src={values.image ?? encodeURI(session.user?.image?.url)}
                      text={values.name ?? session.user?.name}
                      className="border-2 border-solid border-neutral shadow-dropshadow-sm"
                    />
                    <Button
                      size="icon"
                      asChild
                      className="absolute bottom-0 right-0 size-12 border-2 border-solid border-white [&_svg]:size-5"
                    >
                      <span>
                        <FiCamera />
                      </span>
                    </Button>
                  </Label>
                </ProfileSidebar>

                <ProfileForm
                  className="space-y-5"
                  onSubmit={e => updateUser(e)}
                >
                  <FormField
                    label={t('profile.labelFullName')}
                    type="text"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                  />

                  <FormField
                    label="CPF"
                    id="cpf_cnpj"
                    hasError={cpfError}
                    errorText="CPF Inválido"
                  >
                    <InputMask
                      id="cpf_cnpj"
                      name="cpf_cnpj"
                      mask="999.999.999-99"
                      placeholder="Digite o número"
                      value={values.cpf_cnpj}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      aria-invalid={cpfError}
                      disabled={!session.user.profileNames.includes('Administrador') && (session?.user?.cpfCnpj || '') !== ''}
                    />
                  </FormField>

                  <FormField
                    label="Email"
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    disabled={!session.user.profileNames.includes('Administrador')}
                  />

                  <FormField
                    label={t('profile.labelPhone')}
                    id="phone"
                  >
                    <InputMask
                      id="phone"
                      name="phone"
                      mask="(99) 99999-9999"
                      value={values.phone}
                      onChange={handleChange}
                    />
                  </FormField>

                  <FormField
                    label={t('profile.bio')}
                    id="bio"
                  >
                    <Textarea
                      name="bio"
                      id="bio"
                      value={values.bio}
                      onChange={handleChange}
                    />
                  </FormField>

                  {playfulStudent ? (
                    <button
                      type="submit"
                      className="group/btn relative inline-flex items-center justify-center overflow-hidden rounded-full px-8 py-3 text-sm font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
                      style={{
                        background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-third) 88%, #fff) 0%, var(--color-third) 100%)',
                        boxShadow: '0 4px 0 color-mix(in srgb, var(--color-third) 62%, #000), 0 6px 14px rgba(0,0,0,0.16)'
                      }}
                    >
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
                        style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
                      />
                      <span className="relative">{t('button.save')}</span>
                    </button>
                  ) : (
                    <Button
                      type="submit"
                      variant="third"
                      size="sm"
                    >
                      {t('button.save')}
                    </Button>
                  )}
                </ProfileForm>
              </ProfileInfo>
            </TabsContent>

            <TabsContent value="notifications">
              <div className="space-y-5">
                <FormCheck
                  label={t('profile.wantSmsNotification')}
                  name="sendPhoneNotifications"
                  id="sendPhoneNotifications"
                  checked={userDetail?.['send-phone-notifications']}
                  onCheckedChange={() =>
                    updateUserDetail({
                      'send-phone-notifications': !userDetail['send-phone-notifications']
                    })
                  }
                />

                <FormCheck
                  label={t('profile.wantEmailNotification')}
                  name="sendEmailNotifications"
                  id="sendEmailNotifications"
                  checked={userDetail?.['send-email-notifications']}
                  onCheckedChange={() =>
                    updateUserDetail({
                      'send-email-notifications': !userDetail['send-email-notifications']
                    })
                  }
                />

                {(userDetail['send-email-notifications'] || userDetail['send-phone-notifications']) && (
                  <>
                    <h3 className="font-bold text-primary-dark">{t('profile.notificatedByTitle')}</h3>

                    <div className="space-y-5">
                      <FormSwitch
                        label={t('profile.switchCreatedEvent')}
                        checked={notificationsFormik?.values?.['event-create']}
                        onCheckedChange={handleNotificationsChange('event-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchNewMaterial')}
                        checked={notificationsFormik?.values?.['content-create']}
                        onCheckedChange={handleNotificationsChange('content-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchNewPost')}
                        checked={notificationsFormik?.values?.['post-create']}
                        onCheckedChange={handleNotificationsChange('post-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchNewActivity')}
                        checked={notificationsFormik?.values?.['activity-create']}
                        onCheckedChange={handleNotificationsChange('activity-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchNewTask')}
                        checked={notificationsFormik?.values?.['slot-create']}
                        onCheckedChange={handleNotificationsChange('slot-create')}
                      />

                      <FormSwitch
                        label={t('profile.switchAddNewProject')}
                        checked={notificationsFormik?.values?.['project-create']}
                        onCheckedChange={handleNotificationsChange('project-create')}
                      />
                    </div>
                  </>
                )}
              </div>
            </TabsContent>

            <TabsContent value="security">
              <form
                className="space-y-5"
                onSubmit={formik.handleSubmit}
              >
                <FormField
                  label={t('profile.currentPass')}
                  id="password"
                  hasError={formik.touched.password && formik.errors.password}
                  errorText={formik.errors.password}
                >
                  <InputPassword
                    id="password"
                    name="password"
                    placeholder={t('profile.typeYourPass')}
                    aria-invalid={Boolean(formik.touched.password && formik.errors.password)}
                    {...formik.getFieldProps('password')}
                  />
                </FormField>

                <FormField
                  label={t('profile.newPass')}
                  id="newPassword"
                  hasError={formik.touched.newPassword && formik.errors.newPassword}
                  errorText={formik.errors.newPassword}
                >
                  <InputPassword
                    id="newPassword"
                    name="newPassword"
                    placeholder={t('profile.typeYourPass')}
                    aria-invalid={Boolean(formik.touched.newPassword && formik.errors.newPassword)}
                    {...formik.getFieldProps('newPassword')}
                  />
                </FormField>

                <FormField
                  label={t('profile.confirmYourPass')}
                  id="confirmPassword"
                  hasError={formik.touched.confirmPassword && formik.errors.confirmPassword}
                  errorText={formik.errors.confirmPassword}
                >
                  <InputPassword
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder={t('profile.typeYourPass')}
                    aria-invalid={Boolean(formik.touched.confirmPassword && formik.errors.confirmPassword)}
                    {...formik.getFieldProps('confirmPassword')}
                  />
                </FormField>

                <Button
                  type="submit"
                  variant="third"
                  size="sm"
                >
                  {t('button.save')}
                </Button>
              </form>
            </TabsContent>
          </PageContent>

          <PageSidebar variant="md">
            {playfulStudent ? (
              <div className="rounded-[20px] bg-[var(--color-bg-lilac)] p-5 shadow-card">
                <div className="mb-2.5 flex items-center gap-2.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-[0_2px_6px_rgba(68,43,140,0.12)]">
                    <FiShield
                      className="h-5 w-5"
                      style={{ color: 'var(--color-secondary)' }}
                    />
                  </span>
                  <h3
                    className="mb-0 text-base font-extrabold leading-tight"
                    style={{ color: 'var(--color-secondary)' }}
                  >
                    Seus dados protegidos
                  </h3>
                </div>
                <p className="mb-0 text-sm leading-relaxed text-primary-dark [&_a]:font-bold [&_a]:text-primary">{lgpdContent}</p>
              </div>
            ) : (
              <TypographyDisclaimer>{lgpdContent}</TypographyDisclaimer>
            )}
          </PageSidebar>
        </Page>
      </Tabs>

      <Onboarding
        show={showOnboarding}
        setShowOnboarding={setShowOnboarding}
        onClose={() => setShowOnboarding(false)}
        path={path}
        profiles={profiles}
        title={t('onboarding.titleProfile')}
      />
    </DashboardContent>
  );
}
